import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { ChartFacade } from '@mode/charts/contract';
import { ReportRunProgressComponent } from '@mode/report-viewer/util';
import { ChartTypes, DatasetResult, DataViewResponseTypes } from '@mode/shared/contract-common';
import {
  normalizeSwitchView,
  QueryRunResource,
  ReportClientService,
  ReportViewerVisualizationsResponse,
  switchViewResponseToDataView,
} from '@mode/shared/data-access-webapp';
import { isPresent, LegacyRouterService } from '@mode/shared/util-js';
import { ToastService } from '@mode/shared/util-toast';
import { recordEvent } from '@mode/shared/util-tracing';
import { DataViewFacade, SwitchViewActions } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { reportAndViewResponseToFeatureState, switchViewResponseHasDataset } from '../report-viewer-data-mappers';
import * as ReportViewerActions from './report-viewer.actions';
import { ReportViewerDomainState } from './report-viewer.selectors';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { isStillRunning, unsetSwitchViewAttrsIsLocal } from '@mode/switch-views/domain';

@Injectable()
export class ReportViewerEffects {
  /**
   * Note - This large effect is mostly a port of all the logic that was being done in `report.view.data.service.ts`.
   * TODO: We will want to break this up once we have a more clear idea of how we want polling to be involved as well as
   * the expectation around how all of the cascading effects should exist.
   */
  loadFullReport$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportViewerActions.loadFullReport),
        tap((action) =>
          recordEvent('report-loading', 'load-full-report', {
            traceId: action.reportToken,
            runToken: action.reportRunToken,
          })
        ),
        withLatestFrom(this.legacyRouterService.getRouteState()),
        switchMap(([action, routeState]) =>
          this.reportViewerService
            .loadFullReport(action.accountId, action.reportToken, {
              runId: action.reportRunToken,
            })
            .pipe(
              tap((response) => {
                // Transform report data and dispatch action to reduce report
                // Note - for the time being, it's important that this is dispatched first, so the layout and tokens to
                // watch can be reduced into state. As we flesh out how we expect this flow to work, the data views and
                // polling may likely be in subsequent effects to make things more explicit.
                const reportPayload = reportAndViewResponseToFeatureState(
                  response.reportContainerResult,
                  response.reportViewerResult
                );
                recordEvent('report-loading', 'load-full-report-success', {
                  traceId: action.reportToken,
                  runToken: action.reportRunToken,
                  response,
                });
                // with the transformed reportPayload update the store
                this.store.dispatch(ReportViewerActions.loadFullReportSuccess({ report: reportPayload }));

                // Assemble charts by query in order to reduce into state
                const unprocessedVizByQuery: { [key: string]: ChartTypes.ChartResource[] } = {};

                // Get local and imported switchViews, filter for only ones that have been run, then normalize them for convenience in the flow
                // until things are fully refactored with the new endpoints and HAL compliance.
                const localSwitchViews: DataViewResponseTypes.SwitchViewResponses[] =
                  response.reportViewerResult._embedded.local_switch_views._embedded.switch_views;
                const localSwitchViewsWithRuns: DataViewResponseTypes.SwitchViewWithRunsResponses[] =
                  localSwitchViews.filter(
                    (switchViewResponse): switchViewResponse is DataViewResponseTypes.SwitchViewWithRunsResponses =>
                      switchViewResponseHasDataset(switchViewResponse)
                  );
                const importedSwitchViews: DataViewResponseTypes.SwitchViewLastSuccessfulRunResponse[] =
                  response.reportViewerResult._embedded.imported_switch_views._embedded.switch_views;
                const switchViews: DataViewResponseTypes.SwitchViewWithRunsResponses[] =
                  localSwitchViewsWithRuns.concat(importedSwitchViews);
                const normalizedSwitchViews: DataViewResponseTypes.NormalizedSwitchViewResponse[] = switchViews.map(
                  (switchView) => normalizeSwitchView(switchView)
                );

                // Transform dataViews data and dispatch
                const dataViews = normalizedSwitchViews.map(
                  (switchView) => switchViewResponseToDataView(switchView) // handle
                );

                // We need to unset isLocal from the switchView attrs when loading dataViews for explorations
                const updatedDataViews = dataViews.map((dataView) => {
                  const switchView = unsetSwitchViewAttrsIsLocal(dataView.switchView);
                  return { ...dataView, switchView };
                });

                this.dataViewFacade.loadGfDataViews(updatedDataViews);

                // Dispatch unprocessedViz by query so they can be turned into resources in the legacy flow, then dispatched
                // to be reduced into state. This is temporary until all of the legacy code related to resources and the old
                // api is migrated.
                Object.keys(unprocessedVizByQuery).forEach((queryToken) => {
                  this.chartFacade.loadChartsFromGfResources(queryToken, unprocessedVizByQuery[queryToken]);
                });

                // Determine which switch views are still running and start polling unfinished ones
                const switchViewTokensToPoll: string[] = [];
                normalizedSwitchViews.forEach((dataView) => {
                  if (isStillRunning(dataView._embedded.dataset.state)) {
                    switchViewTokensToPoll.push(dataView.token);
                  }
                });

                // We need the reportRunToken for polling. At this point, the reportRunToken on the action
                // is expected to be null if reportRun is not present in the url.
                const actualReportRunToken =
                  action.reportRunToken || response.reportViewerResult._embedded.report_run.token;
                this.dataViewFacade.startPolling(
                  action.accountId,
                  action.reportToken,
                  actualReportRunToken,
                  switchViewTokensToPoll
                );
              })
            )
        )
      );
    },
    { dispatch: false }
  );

  loadReportViewVisualizations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ReportViewerActions.loadReportViewVisualizations,
          ReportViewerActions.loadReportViewVisualizationsFromExploration
        ),
        tap((action) =>
          recordEvent('report-loading', 'load-report-view-visualizations', {
            traceId: action.reportToken,
          })
        ),
        withLatestFrom(this.legacyRouterService.getRouteState()),
        switchMap(([action, routeState]) => {
          const routerVizId = routeState.params['vizId'];
          if (routerVizId) {
            return this.reportViewerService.loadExploreVisualization(action.accountId, action.reportToken, routerVizId);
          } else {
            // call viz api in parallel. No need to wait for report layout
            return this.reportViewerService.loadReportViewVisualizations(action.accountId, action.reportToken).pipe(
              tap((response) =>
                recordEvent('report-loading', 'load-report-view-visualizations-success', {
                  traceId: action.reportToken,
                  response,
                })
              )
            );
          }
        }),
        switchMap((visualizationsResponse) =>
          this.dataViewFacade.datasetResults$.pipe(
            filter((datasetResults) => !!datasetResults.length),
            tap((datasetResults) => this.onLoadReportVisualizations(visualizationsResponse, datasetResults))
          )
        )
      ),
    { dispatch: false }
  );

  pollForNewReportRuns$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportViewerActions.pollForNewReportRuns),
        tap((action) =>
          recordEvent('report-loading', 'fullscreen-poll-report-run', {
            traceId: action.reportToken,
            runToken: action.reportRunToken,
          })
        ),
        switchMap((action) => {
          return timer(0, 5000).pipe(
            takeUntil(this.actions$.pipe(ofType(ReportViewerActions.stopPollingForNewReportRuns))),
            concatMap(() =>
              this.reportViewerService.loadReportViewMostRecentSuccessful(action.accountId, action.reportToken)
            ),
            tap(({ _embedded: { report_run: reportRun } }) => {
              let mostRecentReportRunToken = action.reportRunToken;
              if (reportRun.token !== mostRecentReportRunToken && reportRun.is_latest_successful_report_run) {
                action.onNewReportRun({
                  account_username: action.accountId,
                  last_successful_run_api_url: reportRun._links.latest_successful_report_run_api_url.href,
                  last_successful_run_token: reportRun.token,
                  reportToken: action.reportToken,
                });
                mostRecentReportRunToken = reportRun.token;
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  /**
   * Note - This large effect is mostly a port of all the logic that was being done in `report.view.data.service.ts`.
   * TODO: We will want to break this up once we have a more clear idea of how we want polling to be involved as well as
   * the expectation around how all of the cascading effects should exist.
   */
  loadReportViewRoute$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReportViewerActions.reportViewRouteLoadReportView),
        switchMap((action) =>
          this.reportViewerService.loadReportView(action.accountId, action.reportToken, action.reportRunToken).pipe(
            tap((response) => {
              this.store.dispatch(ReportViewerActions.apiLoadReportViewSuccess({ reportView: response }));

              const unprocessedVizByQuery: { [key: string]: ChartTypes.ChartResource[] } = {};

              // Get local and imported switchViews, filter for only ones that have been run, then normalize them for convenience in the flow
              // until things are fully refactored with the new endpoints and HAL compliance.
              const localSwitchViews: DataViewResponseTypes.SwitchViewResponses[] =
                response._embedded.local_switch_views._embedded.switch_views;
              const localSwitchViewsWithRuns: DataViewResponseTypes.SwitchViewWithRunsResponses[] =
                localSwitchViews.filter(
                  (switchViewResponse): switchViewResponse is DataViewResponseTypes.SwitchViewWithRunsResponses =>
                    switchViewResponseHasDataset(switchViewResponse)
                );
              const importedSwitchViews: DataViewResponseTypes.SwitchViewLastSuccessfulRunResponse[] =
                response._embedded.imported_switch_views._embedded.switch_views;
              const switchViews: DataViewResponseTypes.SwitchViewWithRunsResponses[] =
                localSwitchViewsWithRuns.concat(importedSwitchViews);
              const normalizedSwitchViews: DataViewResponseTypes.NormalizedSwitchViewResponse[] = switchViews.map(
                (switchView) => normalizeSwitchView(switchView)
              );

              // Transform dataViews data and dispatch
              const dataViews = normalizedSwitchViews.map(
                (switchView) => switchViewResponseToDataView(switchView) // handle
              );
              this.dataViewFacade.loadReportViewDataViews(dataViews);

              // Dispatch unprocessedViz by query so they can be turned into resources in the legacy flow, then dispatched
              // to be reduced into state. This is temporary until all of the legacy code related to resources and the old
              // api is migrated.
              Object.keys(unprocessedVizByQuery).forEach((queryToken) => {
                this.chartFacade.loadChartFromReportView(queryToken, unprocessedVizByQuery[queryToken]);
              });

              // Determine which switch views are still running and start polling unfinished ones
              const switchViewTokensToPoll: string[] = [];
              normalizedSwitchViews.forEach((dataView) => {
                if (isStillRunning(dataView._embedded.dataset.state)) {
                  switchViewTokensToPoll.push(dataView.token);
                }
              });

              if (switchViewTokensToPoll.length) {
                this.dataViewFacade.startPolling(
                  action.accountId,
                  action.reportToken,
                  response._embedded.report_run.token,
                  switchViewTokensToPoll
                );
              }
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  loadQueryRun$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportViewerActions.loadQueryRun),
        switchMap((action) =>
          this.reportViewerService
            .loadQueryRun(action.accountId, action.reportToken, action.reportRunToken, action.queryRunToken)
            .pipe(
              map((response: QueryRunResource) =>
                this.dataViewFacade
                  .createSwitchViewTokenByQueryToken(response.query_token)
                  .pipe(filter(isPresent))
                  .subscribe((switchViewToken) =>
                    this.store.dispatch(
                      SwitchViewActions.loadQueryRunSuccess({
                        switchViewToken,
                        rawSource: response.raw_source,
                        renderedSource: response.rendered_source,
                        error: response.error_message,
                      })
                    )
                  )
              ),
              catchError((error) =>
                of(
                  this.store.dispatch(
                    ReportViewerActions.loadQueryRunFailure({
                      error,
                    })
                  )
                )
              )
            )
        )
      ),
    { dispatch: false }
  );

  reportRunPending$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportViewerActions.showLoadingToast),
      map(() => ({
        activeToast: this.toastService.info<unknown, ReportRunProgressComponent>('', {
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: ReportRunProgressComponent,
        }),
      })),
      map(({ activeToast }) => ReportViewerActions.reportLoading({ toastId: activeToast.toastId }))
    )
  );

  reportRunComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportViewerActions.hideLoadingToast),
        map(({ toastId }) => this.toastService.remove(toastId))
      ),
    { dispatch: false }
  );

  /**
   * Callback for when the loadReportViewVisualizations effect is done.
   * Takes care of reducing chart info into the store state
   */
  private onLoadReportVisualizations(
    vizResponse: ChartTypes.ChartResource | ReportViewerVisualizationsResponse,
    datasetResults: DatasetResult[]
  ) {
    const unprocessedVizByQuery: { [key: string]: ChartTypes.ChartResource[] } = {};

    let visualizations: ChartTypes.ChartResource[] = [];
    if ('_embedded' in vizResponse) {
      visualizations = vizResponse._embedded.visualizations;
    } else {
      visualizations = [vizResponse];
    }

    visualizations.forEach((unprocessedViz) => {
      const queryToken = datasetResults.find(
        (dataset) => dataset.switchViewToken === unprocessedViz.switch_view_token
      )?.queryToken;

      if (queryToken) {
        unprocessedVizByQuery[queryToken]
          ? unprocessedVizByQuery[queryToken].push(unprocessedViz)
          : (unprocessedVizByQuery[queryToken] = [unprocessedViz]);
      }
    });
    Object.keys(unprocessedVizByQuery).forEach((queryToken) => {
      this.chartFacade.loadChartsFromGfResources(queryToken, unprocessedVizByQuery[queryToken]);
    });
  }

  constructor(
    private actions$: Actions,
    private reportViewerService: ReportClientService,
    private dataViewFacade: DataViewFacade,
    private chartFacade: ChartFacade,
    private legacyRouterService: LegacyRouterService,
    private store: Store<ReportViewerDomainState>, // Temporary in order to be able to dispatch additional actions from one effect
    private toastService: ToastService
  ) {}
}
